import { useEffect, useRef } from 'react';
import { useSession } from '../lib/auth.ts';
import { identifyUser, resetUser } from './posthog.ts';

/**
 * Keeps PostHog's notion of who is using the app in step with the session.
 *
 * It renders nothing and lives here rather than in the route tree so that
 * analytics stays out of the application's own components.
 */
export function AnalyticsIdentity() {
  const { data: session, isPending } = useSession();
  // `undefined` means "no session observed yet", which is different from `null`
  // — see the reset condition below.
  const observed = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isPending) return;

    const id = session?.user.id ?? null;
    if (id === observed.current) return;

    const previous = observed.current;
    observed.current = id;

    if (id && session) {
      identifyUser(id, session.user.email);
      return;
    }

    // Reset only on an actual sign-out. Calling it for a visitor who arrived
    // signed-out would discard the anonymous id PostHog had just generated, so
    // every page load would look like a different person.
    if (previous) resetUser();
  }, [session, isPending]);

  return null;
}
